import { DeleteForever, Edit, Lock, LockOpen, PictureAsPdf, Send } from '@mui/icons-material'
import { Masonry } from '@mui/lab'
import { Button, Grid } from '@mui/material'
import { DelayedLoadingButton, ExplanationAccordion, GreenText } from 'components'
import { useAuth, useLayout } from 'context'
import * as controllers from 'controllers'
import { InboundTripDto, InboundTripSendToBrokerTransporterDto, InboundTripSendToBrokerTransporterSummaryDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { Fragment, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DriverInformationListCard from './DriverInformationListCard'
import ImportBranchListCard from './ImportBranchListCard'
import InboundTripsAddEditDialog from './InboundTripsAddEditDialog'
import PAPSManagementListCard from './PAPSManagementListCard'
import SendToBrokerTransporterDialog from './SendToBrokerTransporterDialog'
import InboundTripsHeroCard from './InboundTripsHeroCard'

export default function InboundTripsDetails() {
  const navigate = useNavigate()
  const { id } = useParams()
  const layout = useLayout()
  const { permissions } = useAuth()
  const [loadingState, setLoadingState] = useLoadingState({
    isDeletingInboundTrip: false,
    isGettingInboundTrip: false,
    isLockingInboundTrip: false,
    isUnlockingInboundTrip: false,
    isUpdatingInboundTrip: false,
    isGettingSendToBrokerTransporterSummary: false,
    isSendingToBrokerTransporter: false
  })

  const [inboundTripAddEditDialogOpen, setInboundTripAddEditDialogOpen] = useState<boolean>(false)
  const [sendToBrokerDialogOpen, setSendToBrokerDialogOpen] = useState<boolean>(false)
  const [inboundTrip, setInboundTrip] = useState<InboundTripDto>(new InboundTripDto())
  const [sendToBrokerTransporterSummary, setSendToBrokerTransporterSummary] = useState<InboundTripSendToBrokerTransporterSummaryDto>(
    new InboundTripSendToBrokerTransporterSummaryDto()
  )

  useEffect(() => {
    if (id && id.toLowerCase() !== 'undefined' && id.toLowerCase() !== 'new') {
      setLoadingState('isGettingInboundTrip', true)
      controllers
        .getInboundTrip(id)
        .then(response => {
          setInboundTrip(response.value)
        })
        .finally(() => {
          setLoadingState('isGettingInboundTrip', false)
        })
    }
  }, [id])

  useEffect(() => {
    layout.setTitle('Inbound Trip')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(
      loadingState.isDeletingInboundTrip || loadingState.isGettingInboundTrip || loadingState.isLockingInboundTrip || loadingState.isUnlockingInboundTrip
    )
  }, [layout, loadingState.isDeletingInboundTrip, loadingState.isGettingInboundTrip, loadingState.isLockingInboundTrip, loadingState.isUnlockingInboundTrip])

  return (
    <Fragment>
      <InboundTripsAddEditDialog
        initialValues={inboundTrip}
        loading={loadingState.isUpdatingInboundTrip}
        onClose={() => {
          setInboundTripAddEditDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingInboundTrip', true)
          controllers
            .updateInboundTrip(values)
            .then(response => {
              setInboundTrip(response.value)
              setInboundTripAddEditDialogOpen(false)
              enqueueSnackbar('Inbound Trip Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setLoadingState('isUpdatingInboundTrip', false)
            })
        }}
        open={inboundTripAddEditDialogOpen}
      />

      <SendToBrokerTransporterDialog
        initialValues={new InboundTripSendToBrokerTransporterDto()}
        loading={loadingState.isSendingToBrokerTransporter}
        onClose={() => {
          setSendToBrokerDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isSendingToBrokerTransporter', true)
          controllers.inboundTrips
            .sendToBrokerTransporter(id!, values)
            .then(() => {
              enqueueSnackbar('Sent to Broker/Transporter Successfully!', { variant: 'success' })
              setSendToBrokerDialogOpen(false)
            })
            .finally(() => {
              setLoadingState('isSendingToBrokerTransporter', false)
            })
        }}
        open={sendToBrokerDialogOpen}
        summary={sendToBrokerTransporterSummary}
      />

      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <InboundTripsHeroCard inboundTrip={inboundTrip}>
            <Button
              endIcon={<PictureAsPdf />}
              onClick={() => {
                if (inboundTrip.inboundTripGuid) {
                  controllers.inboundTrips.getInboundTripBrokerPdf(inboundTrip.inboundTripGuid)
                }
              }}
              variant='text'
              disabled={!inboundTrip.isLocked}
            >
              PREVIEW BROKER PDF
            </Button>

            <DelayedLoadingButton
              disabled={!inboundTrip.isLocked}
              endIcon={<Send />}
              loading={loadingState.isGettingSendToBrokerTransporterSummary}
              onClick={() => {
                setLoadingState('isGettingSendToBrokerTransporterSummary', true)
                setSendToBrokerTransporterSummary(new InboundTripSendToBrokerTransporterSummaryDto())
                controllers.inboundTrips
                  .getInboundTripSendToBrokerTransporterSummary(inboundTrip.inboundTripGuid!)
                  .then(response => {
                    setSendToBrokerTransporterSummary(response.value)
                    setSendToBrokerDialogOpen(true)
                  })
                  .catch(() => {
                    /* This is needed to prevent an unhandled exception. */
                  })
                  .finally(() => {
                    setLoadingState('isGettingSendToBrokerTransporterSummary', false)
                  })
              }}
              variant='text'
            >
              SEND TO BROKER/TRANSPORTER
            </DelayedLoadingButton>

            <Button
              color='error'
              disabled={!permissions.CANCEL_IMPORT_CAPABILITY || !inboundTrip.inboundTripGuid || inboundTrip.isLocked}
              endIcon={<DeleteForever />}
              onClick={() => {
                if (
                  window.confirm(
                    'Deleting an Inbound Trip cannot be undone, and any PAPS associated with this Inbound Trip will also be deleted from the system. '
                  )
                ) {
                  setLoadingState('isDeletingInboundTrip', true)
                  controllers
                    .deleteInboundTrip(inboundTrip.inboundTripGuid!)
                    .then(() => {
                      enqueueSnackbar('Inbound Trip Deleted Successfully!', { variant: 'success' })
                      navigate('/inbound-trips')
                    })
                    .catch(() => {
                      /*This is needed to prevent an unhandled exception*/
                    })
                    .finally(() => {
                      setLoadingState('isDeletingInboundTrip', false)
                    })
                }
              }}
              variant='text'
              // disabled={inboundTrip.isLocked}
            >
              DELETE TRIP
            </Button>

            <Button
              disabled={loadingState.isLockingInboundTrip || loadingState.isUnlockingInboundTrip}
              endIcon={inboundTrip.isLocked ? <Lock /> : <LockOpen />}
              onClick={() => {
                if (inboundTrip.isLocked === true && window.confirm('Are you sure you wish to unlock this Inbound Trip?')) {
                  setLoadingState('isUnlockingInboundTrip', true)
                  controllers.inboundTrips
                    .unlockInboundTrip(inboundTrip.inboundTripGuid!)
                    .then(response => {
                      setInboundTrip(response.value)
                      enqueueSnackbar('Inbound Trip Unlocked Successfully!', { variant: 'success' })
                    })
                    .catch(() => {
                      /*This is needed to prevent an unhandled exception*/
                    })
                    .finally(() => {
                      setLoadingState('isUnlockingInboundTrip', false)
                    })
                } else {
                  setLoadingState('isLockingInboundTrip', true)
                  controllers.inboundTrips
                    .lockInboundTrip(inboundTrip.inboundTripGuid!)
                    .then(response => {
                      setInboundTrip(response.value)
                      enqueueSnackbar('Inbound Trip Locked Successfully!', { variant: 'success' })
                    })
                    .catch(() => {
                      /*This is needed to prevent an unhandled exception*/
                    })
                    .finally(() => {
                      setLoadingState('isLockingInboundTrip', false)
                    })
                }
              }}
              variant='text'
            >
              {loadingState.isLockingInboundTrip
                ? 'Locking Trip...'
                : loadingState.isUnlockingInboundTrip
                  ? 'Unlocking Trip...'
                  : inboundTrip.isLocked
                    ? 'UNLOCK TRIP'
                    : 'LOCK TRIP'}
            </Button>

            <Button
              endIcon={<Edit />}
              onClick={() => {
                setInboundTripAddEditDialogOpen(true)
              }}
              variant='text'
            >
              EDIT
            </Button>
          </InboundTripsHeroCard>
        </Grid>

        <Grid item xs={12}>
          <Masonry columns={3} sequential sx={{ margin: 0 }}>
            <DriverInformationListCard inboundTrip={inboundTrip} />

            <ImportBranchListCard inboundTrip={inboundTrip} />

            <PAPSManagementListCard inboundTrip={inboundTrip} />
          </Masonry>
        </Grid>

        <Grid item xs={12}>
          <GreenText>
            {`**Hero Card:**

The hero card displays the following fields: Inbound Trip #, Inbound Transporter Company, SCAC, Inbound Transporter Company Email, Entry #, and Entry Date. These values can be updated by selecting the EDIT button in the hero card, which opens the Edit Inbound Trip dialog.

**Lock Trip:**

When Lock Trip is pressed, the system performs several validations: it confirms that a driver has been selected, the Import Branch card contains all required information, all PAPS associated with the inbound trip are locked, and any PAPS for which a Foreign Shippers document was generated have a corresponding signed copy uploaded. If any of these conditions are not met, a warning is displayed and the trip remains unlocked.

If the inbound trip contains only one PAPS and that PAPS includes only one VIN, pressing Lock Trip triggers an additional check: if the Vehicle Plate, Country, or State fields on the Driver Information card are blank, a warning appears stating: “Error: For single vehicle trips, the Vehicle Plate, Country, and State fields cannot be blank and must be filled with the plate information of the vehicle that is being driven across the border.”

If a Foreign Shippers document was generated for a PAPS, a signed copy must be uploaded to that PAPS before the inbound trip can be locked. If a user presses Lock Trip while any PAPS requiring a Signed Foreign Shippers document is missing its upload, the system displays the following error message: “Error: Inbound Trip cannot be locked due to PAPS missing signed foreign shippers.” The Signed Foreign Shippers upload supports the following file types: PDF, PNG, JPG, JPEG.

When Lock Trip is pressed and the inbound trip successfully locks, the following buttons become disabled:

   - Edit on the Driver Information card
   - Edit on the Import Branch card
   - Add PAPS
   - Delete PAPS
  
Pressing Lock Trip also triggers the system to generate the documents that will be sent to the broker and inbound transportation company. The generated documents include:

   - HS-7 Declaration (one per VIN)
   - EPA-3520 (one per VIN)
   - Declaration of Ownership (one for the entire Inbound Trip listing all vehicles in the Inbound Trip)

The "Send to Broker" PDF also includes documents generated when each PAPS in the Inbound Trip was locked:

   - Pro Forma Invoice (one per PAPS, listing all vehicles in the PAPS)
   - Foreign Shippers document (one per PAPS, listing only vehicles with VINs that begin with 1, 4, 5, or 7)

At this point, the “To Broker PDF” file is generated, and the Preview Broker PDF and Send to Broker / Transporter buttons become enabled.

Additionally, when Lock Trip is pressed, all fields in the Edit Inbound Trip dialog become read-only except for Entry # and Entry Date.

**Preview Broker PDF:**

Pressing “Preview Broker PDF” opens a new browser tab showing the “To Broker PDF” file for that Inbound Trip.

**Unlock Trip:**

When Unlock Trip is selected, the following buttons become enabled:

   - Edit on the Driver Information card
   - Edit on the Import Branch card
   - Add PAPS
   - Delete PAPS

The “Preview Broker PDF” and “Send to Broker / Transporter” buttons become disabled.

**Driver Information Card:**

Pressing “Edit” on the Driver Information card opens the “Edit Driver Information” dialog.

**Import Branch Card:**

Pressing “Edit” on the Import Branch card opens the “Edit Import Branch” dialog.

**PAPS Management Card:**

Pressing “Add PAPS” on the PAPS Management card opens the “Add PAPS” dialog.

Pressing “Manage PAPS” on a PAPS card navigates users to the “Manage PAPS” screen.

**Download Abrams Auto Invoice:**

Pressing “Download Abrams Auto Invoice” downloads the Abrams Auto Transport Invoice document to the user’s machine if an Abrams Auto Transport Invoice was OCR’d and associated with the PAPS.`}
          </GreenText>
        </Grid>
      </Grid>
    </Fragment>
  )
}
